import React, {
  createContext,
  SetStateAction,
  Dispatch,
  useContext,
  useMemo,
  useState
} from 'react';
import { DateTime } from 'luxon';
import { DateRange } from 'types/date-range';
import { TimeSignalClassification } from 'graphql/types';
import { SearchCriteria } from 'modules/notes/common/components/SearchFacets';
import { getStartAndEndFromCriterions } from 'modules/notes/common/components/SearchFacets/DateRangeFacet';
import {
  getDateTimeFromIso,
  MAX_SUPPORTED_HISTORICAL_DAYS
} from 'utils/date-time';
import { TIMELINE_PERIOD } from './Timeline';
import { TimesheetPeriodType } from './timesheetPeriod/TimesheetPeriodSelector';

export type NotesClassification = keyof typeof TimeSignalClassification | 'All';

export enum NOTES_PAGE_TYPE {
  LIST,
  TIMELINE
}

export enum NOTES_GROUP_BY_TYPE {
  NONE = 'NONE',
  LABELS = 'LABELS'
}

export type NotesFilterContextValue = {
  selectedDateRange: DateRange;
  selectedPeriod: TimesheetPeriodType;
  selectedClassification: NotesClassification;
  searchCriteria: SearchCriteria;
  pageType: NOTES_PAGE_TYPE;
  timelinePeriod: TIMELINE_PERIOD;
  groupBy: NOTES_GROUP_BY_TYPE;
};

export type FilterContextSetter = Dispatch<
  SetStateAction<NotesFilterContextValue>
>;

type NotesDateRangeContextType = {
  filterContext: NotesFilterContextValue;
  setFilterContext: FilterContextSetter;
};

export const getDefaultFilterContext = (): NotesFilterContextValue => ({
  selectedDateRange: {
    start: DateTime.local(),
    end: DateTime.local()
  },
  selectedPeriod: TimesheetPeriodType.Today,
  selectedClassification: 'All',
  searchCriteria: {
    keywords: [],
    criterions: {}
  },
  pageType: NOTES_PAGE_TYPE.LIST,
  timelinePeriod: TIMELINE_PERIOD.DAY,
  groupBy: NOTES_GROUP_BY_TYPE.NONE
});

export const NotesFilterContext = createContext<NotesDateRangeContextType>({
  filterContext: getDefaultFilterContext(),
  setFilterContext: () => {}
});

export const useNotesFilterContext = (): NotesDateRangeContextType =>
  useContext(NotesFilterContext);

const NotesFilterContextProvider: React.FC = ({
  children
}: {
  children?: React.ReactNode;
}) => {
  const [filterContext, setFilterContext] = useState(getDefaultFilterContext());

  const value = useMemo(() => {
    const result = getStartAndEndFromCriterions(filterContext.searchCriteria);
    const minDate = getDateTimeFromIso(DateTime.local())!.minus({
      days: MAX_SUPPORTED_HISTORICAL_DAYS
    });
    const start = getDateTimeFromIso(result.start);
    const end = getDateTimeFromIso(result.end);
    const computedStart =
      end && end?.minus({ days: MAX_SUPPORTED_HISTORICAL_DAYS }) > minDate
        ? end?.minus({ days: MAX_SUPPORTED_HISTORICAL_DAYS })
        : minDate;

    return {
      filterContext: {
        ...filterContext,
        ...(filterContext.pageType === NOTES_PAGE_TYPE.LIST && {
          selectedDateRange: {
            start: start ?? computedStart,
            end: end
          }
        })
      },
      setFilterContext
    };
  }, [filterContext, setFilterContext]);

  return (
    <NotesFilterContext.Provider value={value}>
      {children}
    </NotesFilterContext.Provider>
  );
};

export default NotesFilterContextProvider;
